import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    ConversionDal,
    LayoutModelBranch,
    PluginContext,
    PluginStageHandler,
    ReadOnlyExtensionAPI,
    MobileAlgoContext
} from '../types'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getTargetLayoutPointer,
    getTransformationsPointer
} from '../conversionDal/utils/pointer'
import type {LayoutEntryItem, StructureEntryItem, TransformationEntryItem} from '../conversionDal/types'
import type {Pointer, SingleLayoutData, AutoResponsivenessMode} from '@wix/document-services-types'
import {LayoutIntentType} from '../../../constants/dsTypesConstants'
import {VIEW_MODES} from '../../../constants/constants'
import {pointerUtils} from '@wix/document-manager-core'
import {Orientation} from '../enums'
import {SemanticTypes} from '../preprocess/conversionDataBuilders'
import type {OrderPluginAPI} from './order/types'

const getDefinedPaths = (obj: Record<string, any>, prefix?: string): Set<string> => {
    const paths = []

    for (const key in obj) {
        if (!obj.hasOwnProperty(key)) {
            continue
        }

        const value = obj[key]
        const path = prefix ? `${prefix}/${key}` : key

        if (Array.isArray(value)) {
            paths.push(path)
        } else if (typeof value === 'object' && value !== null) {
            paths.push(...getDefinedPaths(value, path))
        } else {
            paths.push(path)
        }
    }

    return new Set(paths)
}

export const updateTargetLayout = (
    {conversionDal, componentPointer}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>,
    layout: Partial<SingleLayoutData>
) => {
    conversionDal.modify(getTargetLayoutPointer(componentPointer), (value: LayoutEntryItem) => {
        const definedPaths = getDefinedPaths(layout)

        return {
            ...value,
            ...{
                variableConnections: value?.variableConnections?.filter(({path}) => !definedPaths.has(path))
            },
            ...layout
        }
    })
}

export const updateTransformations = (
    {conversionDal, componentPointer}: ComponentStageData,
    transformation: Partial<TransformationEntryItem>
) => {
    conversionDal.modify(getTransformationsPointer(componentPointer), (value: TransformationEntryItem) => ({
        ...value,
        ...transformation
    }))
}

export const createAdaptiveHeuristic =
    <T extends ComponentStageData>(
        fallbackCallback: PluginStageHandler<T>,
        adaptiveCallback?: PluginStageHandler<T>
    ): PluginStageHandler<T> =>
    (stageData: T, contextHelper: PluginContext) => {
        const {initialSnapshot, componentPointer} = stageData

        if (!initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))) {
            return fallbackCallback(stageData, contextHelper)
        }

        if (adaptiveCallback) {
            return adaptiveCallback(stageData, contextHelper)
        }
    }

export const isSection = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>) => {
    const component = conversionDal.get(componentPointer)
    return [SemanticTypes.SECTION, SemanticTypes.HEADER_SECTION].includes(component.semanticType)
}

export const getParent = (conversionDal: ConversionDal, componentPointer: Pointer): Pointer | undefined => {
    const structure: StructureEntryItem = conversionDal.get(componentPointer)
    const parentPointer = pointerUtils.getPointerFromPointer(structure.parent, componentPointer)

    if (!conversionDal.get(parentPointer)) {
        return
    }

    return parentPointer
}

export const isSlottedComponent = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {conversionDal, componentPointer} = componentStageData
    return conversionDal.get(componentPointer)?.isSlottedComponent
}

export const isPlugin = (componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    const {conversionDal, componentPointer} = componentStageData
    const parent = getParent(conversionDal, componentPointer)

    return parent && isSlottedComponent({...componentStageData, componentPointer: parent})
}

export const isMenuContainerChild = (componentPointer: Pointer, conversionDal: ConversionDal) => {
    const parent = getParent(conversionDal, componentPointer)
    if (!parent) return false
    const grandParent = getParent(conversionDal, parent)
    if (!grandParent) return false
    const component = conversionDal.get(grandParent)
    return component.semanticType === SemanticTypes.MENU
}

export const isInternalBox = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) =>
    conversionDal.get(componentPointer)?.semanticType === SemanticTypes.INTERNAL_BOX

export const isBoxPlugin = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) =>
    conversionDal.get(componentPointer)?.semanticType === SemanticTypes.BOX_PLUGIN

export const isEmptyBox = (componentStageData: ComponentStageData) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)

    return (isInternalBox(componentStageData) || isBoxPlugin(componentStageData)) && !component.components?.length
}

export const isSuperGrid = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    return conversionDal.get(componentPointer)?.layoutIntent?.type === LayoutIntentType.SuperGrid
}

export const isCssGrid = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    return conversionDal.get(componentPointer)?.layoutIntent?.type === LayoutIntentType.CssGrid
}

export const isSuperGridCell = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    const parentPointer = getParent(conversionDal, componentPointer)
    return (
        !!parentPointer &&
        conversionDal.get(componentPointer)?.semanticType === SemanticTypes.BOX &&
        isSuperGrid({componentPointer: parentPointer, conversionDal})
    )
}

export const isSuperGridCellRootContainer = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    const parentPointer = getParent(conversionDal, componentPointer)
    return (
        !!parentPointer &&
        conversionDal.get(componentPointer)?.semanticType === SemanticTypes.INTERNAL_BOX &&
        isSuperGridCell({componentPointer: parentPointer, conversionDal})
    )
}

export const isSectionsRootContainer = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {conversionDal, componentPointer} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)

    return (
        !!parentPointer &&
        isInternalBox(componentStageData) &&
        [SemanticTypes.HEADER_SECTION, SemanticTypes.SECTION].includes(conversionDal.get(parentPointer).semanticType)
    )
}

export const isHeaderContainer = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {conversionDal, componentPointer} = componentStageData

    if (!isSectionsRootContainer(componentStageData)) {
        return false
    }

    const parentPointer = getParent(conversionDal, componentPointer)

    if (!parentPointer) {
        return false
    }

    return conversionDal.get(parentPointer)?.semanticType === SemanticTypes.HEADER_SECTION
}

export const isChildOfHeader = (componentPointer: Pointer, conversionDal: ConversionDal) => {
    const parentPointer = getParent(conversionDal, componentPointer)
    if (!parentPointer) return false
    return isHeaderContainer({
        componentPointer: parentPointer,
        conversionDal
    })
}

export const isHeaderFirstRowChild = (componentPointer: Pointer, conversionDal: ConversionDal) => {
    if (!isChildOfHeader(componentPointer, conversionDal)) return false
    const {itemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    return itemLayout?.gridArea?.rowStart === 1
}

export const isHeaderFirstRowChildComponentSpecialCase = (
    componentPointer: Pointer,
    conversionDal: ConversionDal,
    globalContext: MobileAlgoContext,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    if (!isHeaderFirstRowChild(componentPointer, conversionDal)) return false
    const {order} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const orderApi = order as OrderPluginAPI
    return orderApi.isHeaderSpecialCase()
}

export const isPinned = (componentStagedData: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>) => {
    const {conversionDal, componentPointer} = componentStagedData
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    return layout?.itemLayout?.type === 'FixedItemLayout' || layout?.itemLayout?.pinnedToContainer === true
}

export const isPinnedToContainer = (componentStagedData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStagedData
    return !!getParent(conversionDal, componentPointer)
}

export const isEmptyRootContainer = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)
    return isSectionsRootContainer(componentStageData) && !component.components?.length
}

export const isTinyOrEmptySection = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)

    if ([SemanticTypes.HEADER_SECTION, SemanticTypes.SECTION].includes(component.semanticType)) {
        const [childId] = component.components

        if (conversionDal.get(getMeasurementsPointer(componentPointer)).height <= 10 || childId === undefined) {
            return true
        }

        return isEmptyRootContainer({
            componentPointer: getStructurePointer({id: childId}),
            conversionDal
        })
    }

    return false
}

export const isBigSection = (componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)

    if ([SemanticTypes.HEADER_SECTION, SemanticTypes.SECTION].includes(component.semanticType)) {
        return conversionDal.get(getMeasurementsPointer(componentPointer)).height > 10000
    }

    return false
}

const COMPONENTS_LIMIT = 300

const countDescendants = (conversionDal: ConversionDal, componentPointer: Pointer): number => {
    const component = conversionDal.get(componentPointer)
    const children: string[] = component?.components ?? []
    return children.reduce(
        (count, childId) => count + countDescendants(conversionDal, getStructurePointer({id: childId})),
        children.length
    )
}

export const hasTooManyComponents = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
) => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)

    if (component.semanticType === SemanticTypes.SECTION) {
        return countDescendants(conversionDal, componentPointer) > COMPONENTS_LIMIT
    }

    return false
}

export const predicatePinnedHeuristic = (
    componentStagedData: ComponentStageData,
    horizontalOrVertical: Orientation,
    min: number,
    max: number | undefined,
    predicate: (justifySelf: 'start' | 'center' | 'end', alignSelf: 'start' | 'center' | 'end') => boolean
) => {
    const {conversionDal, componentPointer} = componentStagedData
    if (!isPinned(componentStagedData)) return false
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout} = layout
    const {justifySelf, alignSelf, margins} = itemLayout
    const {left, right, bottom, top} = margins
    const marginVal =
        horizontalOrVertical === Orientation.Horizontal ? left?.value || right?.value : top?.value || bottom?.value
    return predicate(justifySelf, alignSelf) && marginVal >= min && (!max || marginVal <= max)
}

export const findHeuristic = (
    heuristics: ComponentStageHeuristicDefinition[],
    componentStageData: ComponentStageData,
    contextHelper?: PluginContext,
    readonlyExtensionAPI?: ReadOnlyExtensionAPI
): ComponentStageHeuristicDefinition =>
    heuristics.find(({predicate}) => predicate(componentStageData, contextHelper, readonlyExtensionAPI))!

export const findHeuristicFromTree = (
    layoutModelTree: LayoutModelBranch[],
    componentStageData: ComponentStageData,
    contextHelper?: PluginContext,
    readOnlyExtensionAPI?: ReadOnlyExtensionAPI
): ComponentStageHeuristicDefinition | undefined => {
    const branch = layoutModelTree.find(({scope}) => scope(componentStageData))
    if (!branch) {
        return
    }
    return findHeuristic(branch.heuristics, componentStageData, contextHelper, readOnlyExtensionAPI)
}

export const generatePluginHandlerPerMode =
    (
        modeToRunnerMap: Record<AutoResponsivenessMode, PluginStageHandler<ComponentStageData>>
    ): PluginStageHandler<ComponentStageData> =>
    (componentStageData: ComponentStageData, contextHelper: PluginContext) =>
        modeToRunnerMap[componentStageData.config.mode]?.(componentStageData, contextHelper)

export const isBackgroundComponent = ({conversionDal, componentPointer}: ComponentStageData): boolean => {
    const layout = conversionDal.get(getSourceLayoutPointer(componentPointer))
    return layout?.itemLayout?.type === 'BackgroundLayerItemLayout'
}

export const isNonUnifiedPlugin = (componentPointer: Pointer, conversionDal: ConversionDal) =>
    !isInternalBox({conversionDal, componentPointer}) && isPlugin({componentPointer, conversionDal})

export const isStackContainerLayout = ({
    initialSnapshot,
    componentPointer
}: Pick<ComponentStageData, 'initialSnapshot' | 'componentPointer'>): boolean =>
    initialSnapshot.getValue(getSourceLayoutPointer(componentPointer))?.containerLayout?.type === 'StackContainerLayout'

export const isGridContainerLayout = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>): boolean => {
    const targetContainerType = conversionDal.get(getTargetLayoutPointer(componentPointer))?.containerLayout?.type
    return !targetContainerType || targetContainerType === 'GridContainerLayout'
}

export const isChildOfStackContainer = (
    componentStageData: Pick<ComponentStageData, 'conversionDal' | 'componentPointer' | 'initialSnapshot'>
): boolean => {
    const {conversionDal, componentPointer, initialSnapshot} = componentStageData
    const parentPointer = getParent(conversionDal, componentPointer)
    if (!parentPointer) return false
    return isStackContainerLayout({initialSnapshot, componentPointer: parentPointer})
}

const CONTAINER_TO_ITEM_LAYOUT_TYPE: Record<string, string> = {
    GridContainerLayout: 'GridItemLayout',
    StackContainerLayout: 'StackItemLayout'
}

export const getItemLayoutType = ({
    conversionDal,
    componentPointer,
    initialSnapshot
}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer' | 'initialSnapshot'>): string => {
    const parentPointer = getParent(conversionDal, componentPointer)
    if (!parentPointer) return CONTAINER_TO_ITEM_LAYOUT_TYPE.GridContainerLayout
    const containerLayoutType = initialSnapshot.getValue(getSourceLayoutPointer(parentPointer))?.containerLayout?.type
    return CONTAINER_TO_ITEM_LAYOUT_TYPE[containerLayoutType]
}

export const shouldBeHorizontallyStretched = (
    {
        conversionDal,
        componentPointer,
        initialSnapshot,
        config
    }: Pick<ComponentStageData, 'conversionDal' | 'componentPointer' | 'initialSnapshot' | 'config'>,
    _contextHelper?: PluginContext,
    readOnlyExtensionApi?: ReadOnlyExtensionAPI
): boolean => {
    if (!getParent(conversionDal, componentPointer)) return false
    const sourceLayout = initialSnapshot.getValue(getSourceLayoutPointer(componentPointer))
    const isHorizontallyStretched =
        sourceLayout?.itemLayout?.justifySelf === 'stretch' ||
        readOnlyExtensionApi?.layout.isStretchedWidth(
            pointerUtils.getCompPointer(componentPointer.id, VIEW_MODES.DESKTOP, {variants: config.targetVariants})
        )

    return !!isHorizontallyStretched
}

export const shouldBeVerticallyStretched = (
    {
        conversionDal,
        componentPointer,
        initialSnapshot,
        config
    }: Pick<ComponentStageData, 'conversionDal' | 'componentPointer' | 'initialSnapshot' | 'config'>,
    _contextHelper?: PluginContext,
    readOnlyExtensionApi?: ReadOnlyExtensionAPI
): boolean => {
    if (!getParent(conversionDal, componentPointer)) return false
    const sourceLayout = initialSnapshot.getValue(getSourceLayoutPointer(componentPointer))
    const isVerticallyStretched =
        sourceLayout?.itemLayout?.alignSelf === 'stretch' ||
        readOnlyExtensionApi?.layout.isStretchedHeight(
            pointerUtils.getCompPointer(componentPointer.id, VIEW_MODES.DESKTOP, {variants: config.targetVariants})
        )

    return !!isVerticallyStretched
}
